import { INotificationsState } from 'config/notificationTypes'
import { INotification } from 'services/notifications/types'
import { isSyntheticChronoKey } from 'services/notifications/normalizeNotification'
import {
  fetchNotificationsInbox,
  fetchUnreadCount,
  fetchUnreadNotifications,
  markNotificationsSeen,
} from 'services/notifications/notificationsApi'

type ListName = 'inbox' | 'unread'

export const NOTIFICATIONS_PAGE_SIZE = 20

export const actionTypes = {
  SET_LIST_LOADING: '[NOTIFICATIONS] SET_LIST_LOADING',
  SET_LIST: '[NOTIFICATIONS] SET_LIST',
  SET_UNREAD_COUNT: '[NOTIFICATIONS] SET_UNREAD_COUNT',
  MARK_SEEN_OPTIMISTIC: '[NOTIFICATIONS] MARK_SEEN_OPTIMISTIC',
  RESTORE_STATE: '[NOTIFICATIONS] RESTORE_STATE',
  RECEIVE_LIVE_NOTIFICATION: '[NOTIFICATIONS] RECEIVE_LIVE_NOTIFICATION',
}

const UNREAD_COUNT_DEDUPE_MS = 5000
let unreadCountRequest: Promise<number> | null = null
let lastUnreadCountRequestAt = 0

export const handleFetchUnreadCount = () => async (dispatch: any) => {
  const now = Date.now()

  if (unreadCountRequest) return unreadCountRequest
  if (now - lastUnreadCountRequestAt < UNREAD_COUNT_DEDUPE_MS) return null

  lastUnreadCountRequestAt = now
  unreadCountRequest = fetchUnreadCount()

  try {
    const unreadCount = await unreadCountRequest
    dispatch({ type: actionTypes.SET_UNREAD_COUNT, unreadCount })
    return unreadCount
  } catch (err) {
    console.warn('[Notifications] failed to fetch unread count:', err)
    return null
  } finally {
    unreadCountRequest = null
  }
}

const fetchList =
  (list: ListName, fetcher: (limit: number, cursor: string) => Promise<{ items: any[]; next_cursor: string | null }>) =>
  ({ append = false }: { append?: boolean } = {}) =>
  async (dispatch: any, getState: any) => {
    try {
      const existing = getState().notifications[list] as INotificationsState['inbox']
      const cursor = append ? existing?.nextCursor || '' : ''

      dispatch({ type: actionTypes.SET_LIST_LOADING, list, isLoading: true })
      const { items, next_cursor } = await fetcher(NOTIFICATIONS_PAGE_SIZE, cursor)

      dispatch({ type: actionTypes.SET_LIST, list, items, nextCursor: next_cursor, append })
    } catch (err) {
      dispatch({ type: actionTypes.SET_LIST_LOADING, list, isLoading: false })
      console.warn(`[Notifications] failed to fetch ${list}:`, err)
    }
  }

export const handleFetchInbox = fetchList('inbox', fetchNotificationsInbox)
export const handleFetchUnread = fetchList('unread', fetchUnreadNotifications)

export const handleMarkSeen =
  (params: { seenKeys: string[] } | { markAll: true }) => async (dispatch: any, getState: any) => {
    const seenKeys = 'seenKeys' in params ? params.seenKeys : null
    const markAll = 'markAll' in params

    const notificationsBefore = getState().notifications
    dispatch({ type: actionTypes.MARK_SEEN_OPTIMISTIC, seenKeys, markAll })

    // A live-arrived push notification that never carried a real backend id gets a synthetic
    // client-only chrono_key (normalizeNotification's `${ts}#notification` fallback). Sending that
    // to the mark-seen endpoint 500s — the backend has no matching record. The optimistic dispatch
    // above already flipped it locally (that's internally consistent either way), so just drop
    // synthetic keys before the round-trip instead of letting the request fail.
    const realSeenKeys = seenKeys?.filter((key) => !isSyntheticChronoKey(key)) ?? null
    if (!markAll && seenKeys?.length && !realSeenKeys?.length) {
      console.warn('[Notifications] skipped mark-seen request — all keys were client-generated fallbacks:', seenKeys)
      return
    }

    try {
      const result = await markNotificationsSeen(markAll ? params : { seenKeys: realSeenKeys as string[] })
      dispatch({ type: actionTypes.SET_UNREAD_COUNT, unreadCount: result.unread_count })
    } catch (err) {
      dispatch({ type: actionTypes.RESTORE_STATE, state: notificationsBefore })
      console.warn('[Notifications] failed to mark seen:', err)
    }
  }

// A push arrived and the SW handed it to us directly (focused-tab toast, or an OS notification
// clicked while some tab was already open — see usePushNotificationMessages). It only exists in
// local toast/click-handling state until now; without inserting it here too, the Notification
// Center's inbox/unread lists don't contain it, so opening the Center right after shows a stale
// list missing this item, and marking it seen optimistically is a no-op (the reducer has nothing
// with a matching chrono_key to flip).
export const handleReceiveLiveNotification = (notification: INotification) => (dispatch: any) => {
  dispatch({ type: actionTypes.RECEIVE_LIVE_NOTIFICATION, notification })
}
